import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { RouterModule } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { PublicStakeholderDTO } from 'src/app/features/subscription/types/PublicStakeholderDTO';
import { LoadingSpinnerOverlayComponent } from 'src/app/shared/components/loading-spinner-overlay/loading-spinner-overlay.component';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { RsvpDisplay } from 'src/app/shared/types/RsvpDisplay';
import { SubscriptionSourceType } from 'src/app/shared/types/SubscriptionSourceType';
import { GlobalSearchStakeholderComponent } from '../../../../shared/components/global-search-stakeholder/global-search-stakeholder.component';
import { RsvpPageService } from '../../rsvp-page.service';
import { PublicMeetingDTO } from 'src/app/shared/types/PublicMeetingDTO';
import { SubscriptionService } from 'src/app/features/subscription/subscription.service';

@Component({
  selector: 'app-rsvp-search-component',
  standalone: true,
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' },
    },
  ],
  imports: [
    CommonModule,
    RouterModule,
    MatInputModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    LoadingSpinnerOverlayComponent,
    GlobalSearchStakeholderComponent,
  ],

  templateUrl: './rsvp-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../rsvp.component.scss', './rsvp-search.component.scss'],
})
export class RsvpSearchComponent {
  private subscription: Subscription | null = null;

  rsvpDisplay: RsvpDisplay | undefined;
  isStakeholderSearchValid: boolean = false;
  onStakeholderSearch: number = 0;

  isPostInProgress$: Observable<boolean>;

  constructor(
    private _rsvpService: RsvpPageService,
    private subscriptionService: SubscriptionService,
    private urlParamsService: UrlParamsService
  ) {
    this.rsvpDisplay = _rsvpService.setupFormDisplay();
    this.isPostInProgress$ = this._rsvpService.isPostInProgress$;
  }

  async onRsvpClicked(): Promise<void> {
    // Emits a value to the stake search component to run the search API call
    // Triggers handleStakeholderSearchResult to run
    this.onStakeholderSearch++;
  }

  handleFormValidity(event: boolean): void {
    this.isStakeholderSearchValid = event;
  }

  handleStakeholderSearchResult(
    event: {
      status: string;
      data: any;
      formValues: any;
    },
    meeting: PublicMeetingDTO,
    display: RsvpDisplay
  ): void {
    switch (event.status) {
      case 'success':
        if (event.data && event.data.StakeGuid) {
          this._rsvpService
            .post(meeting.MeetingGuid, event.data.StakeGuid, display.controls.peopleCount.value)
            .pipe()
            .subscribe({
              complete: () => {
                this.urlParamsService.setProjectGuid(meeting.ProjectGuid);
                this.urlParamsService.navigateWithParams('/rsvp/confirm', {
                  LastName: event.formValues.LastName,
                  Mail1ZipCode: event.formValues.Mail1ZipCode,
                  StakeEmail: event.formValues.StakeEmail,
                  SubscribeSourceId: SubscriptionSourceType.RSVP,
                  isEdit: false,
                  backUrl: '/rsvp/search',
                  IsReceiveEmailUpdate: false,
                  TotalAttendees: display.controls.peopleCount.value,
                  MeetingGuid: meeting.MeetingGuid,
                } satisfies PublicStakeholderDTO & {
                  isEdit: boolean;
                  backUrl: string;
                  TotalAttendees: number;
                  MeetingGuid: string;
                });
              },
              error: (err) => {
                console.error(err);
              },
            });
        } else {
          this.urlParamsService.setProjectGuid(meeting.ProjectGuid);
          this.urlParamsService.navigateWithParams('/rsvp/edit', {
            LastName: event.formValues.LastName,
            Mail1ZipCode: event.formValues.Mail1ZipCode,
            StakeEmail: event.formValues.StakeEmail,
            SubscribeSourceId: SubscriptionSourceType.RSVP,
            isEdit: false,
            backUrl: '/rsvp/search',
            IsReceiveEmailUpdate: false,
            TotalAttendees: display.controls.peopleCount.value,
            MeetingGuid: meeting.MeetingGuid,
          } satisfies PublicStakeholderDTO & {
            isEdit: boolean;
            backUrl: string;
            TotalAttendees: number;
            MeetingGuid: string;
          });
        }

        break;
      case 'error':
        console.error('Error fetching stakeholder:', event.data);
        this.urlParamsService.navigateToErrorPage('Error searching for stakeholder.');
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
